import type { CSSProperties, Dispatch, SetStateAction } from 'react';
import { useCallback } from 'react';
import { CONFIG } from '@frameio/config';
import type {
  PaymentCard,
  PlanFeatureInput,
} from '@frameio/graphql-client/generated';
import { useAccountPermissions } from '@frameio/permissions';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsIntegrationClient } from '@frameio/shared/contexts/ClientNameContext';
import {
  useIsEnterpriseSubscriber,
  useOpenCalendly,
} from '@frameio/shared/hooks';
import { useOpenInBrowserMutation } from '@frameio/shared/integrations/gql';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { getV4AppServerOriginFromIntegrations } from '@frameio/shared/routing/redirectToV4';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { formatMoney } from '@frameio/utils/formatters/formatters';
import { Button2, noop, Text } from '@frameio/vapor';
import { IconStarFantasyFill } from '@frameio/vapor-icons';
import {
  getTierConfig,
  segmentTrackingValues,
} from '../../../feature-gate/config';
import { useManagedByAdobe } from '../../../feature-gate/hooks/useManagedByAdobe';
import { getPeriodText } from '../../../plans/constants';
import { StyledLink } from '../../../shared/components/StyledLink';
import { UpgradeDialogLayout } from '../../../shared/components/UpgradeDialogLayout';
import { ACCOUNT_CONVERSION_STEPS } from './constants';
import TEXT from './generated/TEXT/PlanDetails';
import { useAccountOwner } from './hooks/useAccountOwner';
import styles from './PlanDetails.module.css';
import { PlanUpgradeFeatures } from './PlanUpgradeFeatures';
import type { ConversionDialogProps } from './types';

TEXT.DEFINE(
  {
    /** Button text to chat with sales team */
    chatWithSales: 'Chat with Sales',
    /** Header text for subscribe button */
    upgradeHeader: (title: string) => `Subscribe to ${title}`,
    /** Text prefix before plan title */
    upgradeTo: 'Upgrade to',
    /** Text when feature not available on managed by Adobe plan */
    featureNotAvailableAdobe: 'This feature is not available on your plan.',
    /** Text to contact admin for Adobe plans */
    contactAdminAdobe: 'Contact your admin for assistance.',
    /** Text to contact owner to upgrade - includes owner name and email */
    contactOwnerToUpgrade: (ownerName: string, ownerEmail: string) => (
      <>
        Contact{' '}
        <Text
          color="primary"
          display="inline"
          style={{
            overflowWrap: 'break-word',
          }}
          variant="secondary"
        >
          {ownerName}
        </Text>{' '}
        <StyledLink
          $inlineBlock={true}
          href={`mailto:${ownerEmail}`}
          target="_blank"
        >
          {`(${ownerEmail})`}
        </StyledLink>{' '}
        to upgrade to this plan.
      </>
    ),
    /** Price text for enterprise tier */
    priceEnterprise: 'Custom pricing · ',
    /** Price text for paid plans */
    pricePaid: (formattedPrice: string, period: string) =>
      `${formattedPrice}/ ${period} + tax per user · `,
    /** Link text for enterprise tier */
    comparePlansEnterprise: 'See Current Plan Details',
    /** Link text for other tiers */
    comparePlans: 'Compare plans',
    /** Close button label */
    close: 'Close',
    /** Sub-header texts for each feature unlock */
    subHeaders: {
      /** Sub-header for allowed domains feature */
      allowedDomains: 'Unlock Allowed Domains',
      /** Sub-header for archival storage feature */
      archivalStorage: 'Unlock Archival Storage',
      /** Sub-header for asset lifecycle management feature */
      assetLifecycleManagement: 'Unlock Asset Lifecycle Management',
      /** Sub-header for comment attachments feature */
      commentAttachments: 'Unlock Comment Attachments',
      /** Sub-header for custom branded emails feature */
      customBrandedEmails: 'Unlock Custom-Branded Emails',
      /** Sub-header for custom branded presentations feature */
      customBrandedPresentations: 'Unlock Custom-branded Presentations',
      /** Sub-header for custom branded shares feature */
      customBrandedShares: 'Unlock Custom-Branded Shares',
      /** Sub-header for custom sort feature */
      customSort: 'Unlock Custom Sort',
      /** Sub-header for devices feature */
      devices: 'Unlock Devices',
      /** Sub-header for disable team member invites feature */
      disableTeamMemberInvites: 'Unlock Disabling Team Member Invites',
      /** Sub-header for DRM feature */
      drm: 'Unlock DRM',
      /** Sub-header for forensic watermarking feature */
      forensicWatermarking: 'Unlock Forensic Watermarking',
      /** Sub-header for 4K playback feature */
      fourKPlayback: 'Unlock 4K UHD Playback',
      /** Sub-header for HDR feature */
      hdr: 'Unlock HDR',
      /** Sub-header for mounted storage feature */
      mountedStorage: 'Unlock Mounted Storage',
      /** Sub-header for mounted storage overprovisioning feature */
      mountedStorageOverprovisioningAllowed:
        'Unlock Mounted Storage Overprovisioning',
      /** Sub-header for natural language search feature */
      naturalLanguageSearch: 'Unlock Natural Language Search',
      /** Sub-header for passphrase-protected shares feature */
      passwordProtectedShares: 'Unlock Passphrase-Protected Shares',
      /** Sub-header for restricted folders feature */
      restrictedFolders: 'Unlock Restricted Folders',
      /** Sub-header for restricted projects feature */
      restrictedProjects: 'Unlock Restricted Projects',
      /** Sub-header for reel player feature */
      reelPlayer: 'Unlock Reel Layout',
      /** Sub-header for content security settings feature */
      resourceControlPolicies: 'Unlock Content Security Settings',
      /** Sub-header for secure sharing feature */
      secureSharing: 'Unlock Secure Sharing',
      /** Sub-header for semantic search feature */
      semanticSearch: 'Unlock Semantic Search',
      /** Sub-header for session-based watermarking feature */
      sessionBasedWatermarking: 'Unlock Session-Based Watermarking',
      /** Sub-header for share link expiration feature */
      shareLinkExpiration: 'Unlock Share Link Expiration',
      /** Sub-header for share link security feature */
      shareLinkSecurity: 'Unlock Share Link Security & Permissions',
      /** Sub-header for share lists feature */
      shareLists: 'Unlock Share Lists',
      /** Sub-header for static watermark feature */
      staticWatermark: 'Unlock Static Watermark',
      /** Sub-header for storage connect feature */
      storageConnect: 'Unlock Storage Connect',
      /** Sub-header for internal comments feature */
      teamOnlyComments: 'Unlock Internal Comments',
      /** Sub-header for transcription feature */
      transcription: 'Unlock Transcription',
      /** Sub-header for unlimited watermark templates feature */
      unlimitedWatermarkTemplates: 'Unlock Unlimited Watermark Templates',
      /** Sub-header for upload links feature */
      uploadLinks: 'Unlock Upload Links',
      /** Sub-header for user groups feature */
      userGroups: 'Unlock User Groups',
      /** Sub-header for user management feature */
      userManagement: 'Unlock User Management',
    },
    /** Enterprise badge text */
    enterprise: 'Enterprise',
    /** Add-on badge text */
    addOn: 'Add-On',
  },
  { Text, StyledLink }
);

const getSubHeaders = (): Partial<Record<PlanFeatureInput, string>> => ({
  ALLOWED_DOMAINS: TEXT.subHeaders.allowedDomains,
  ARCHIVAL_STORAGE: TEXT.subHeaders.archivalStorage,
  ASSET_LIFECYCLE_MANAGEMENT: TEXT.subHeaders.assetLifecycleManagement,
  COMMENT_ATTACHMENTS: TEXT.subHeaders.commentAttachments,
  CUSTOM_BRANDED_EMAILS: TEXT.subHeaders.customBrandedEmails,
  CUSTOM_BRANDED_PRESENTATIONS: TEXT.subHeaders.customBrandedPresentations,
  CUSTOM_BRANDED_SHARES: TEXT.subHeaders.customBrandedShares,
  CUSTOM_SORT: TEXT.subHeaders.customSort,
  DEVICES: TEXT.subHeaders.devices,
  DISABLE_TEAM_MEMBER_INVITES: TEXT.subHeaders.disableTeamMemberInvites,
  DISABLE_SHARE_COMMENTS: TEXT.subHeaders.shareLinkSecurity,
  DISABLE_SHARE_DOWNLOADS: TEXT.subHeaders.shareLinkSecurity,
  DISABLE_SHARE_PREVIOUS_VERSIONS: TEXT.subHeaders.shareLinkSecurity,
  DRM: TEXT.subHeaders.drm,
  FORENSIC_WATERMARKING: TEXT.subHeaders.forensicWatermarking,
  FOUR_K_PLAYBACK: TEXT.subHeaders.fourKPlayback,
  HDR: TEXT.subHeaders.hdr,
  MOUNTED_STORAGE: TEXT.subHeaders.mountedStorage,
  MOUNTED_STORAGE_OVERPROVISIONING_ALLOWED:
    TEXT.subHeaders.mountedStorageOverprovisioningAllowed,
  NATURAL_LANGUAGE_SEARCH: TEXT.subHeaders.naturalLanguageSearch,
  PASSWORD_PROTECTED_SHARES: TEXT.subHeaders.passwordProtectedShares,
  PRIVATE_PROJECTS: TEXT.subHeaders.restrictedProjects,
  REEL_PLAYER: TEXT.subHeaders.reelPlayer,
  RESOURCE_CONTROL_POLICIES: TEXT.subHeaders.resourceControlPolicies,
  RESTRICTED_FOLDERS: TEXT.subHeaders.restrictedFolders,
  SECURE_SHARING: TEXT.subHeaders.secureSharing,
  SEMANTIC_SEARCH: TEXT.subHeaders.semanticSearch,
  SESSION_BASED_WATERMARKING: TEXT.subHeaders.sessionBasedWatermarking,
  SHARE_LINK_EXPIRATION: TEXT.subHeaders.shareLinkExpiration,
  STATIC_WATERMARK: TEXT.subHeaders.staticWatermark,
  STORAGE_CONNECT: TEXT.subHeaders.storageConnect,
  TEAM_ONLY_COMMENTS: TEXT.subHeaders.teamOnlyComments,
  TRANSCRIPTION: TEXT.subHeaders.transcription,
  UNLIMITED_WATERMARK_TEMPLATES: TEXT.subHeaders.unlimitedWatermarkTemplates,
  UPLOAD_LINKS: TEXT.subHeaders.uploadLinks,
  USER_GROUPS: TEXT.subHeaders.userGroups,
  USER_MANAGEMENT: TEXT.subHeaders.userManagement,
  SHARE_LISTS: TEXT.subHeaders.shareLists,
});

const images: Record<string, string> = {
  COMMENT_ATTACHMENTS: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-comments3.png`,
  CUSTOM_BRANDED_SHARES: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-branded-header3.png`,
  PRIVATE_PROJECTS: `${CONFIG.staticUrl}/feature-gate/restricted_projects_portrait.png`,
  REEL_PLAYER: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-reel3.png`,
  RESOURCE_CONTROL_POLICIES: `${CONFIG.staticUrl}/feature-gate/enterprise_generic_portrait.png`,
  SECURE_SHARING: `${CONFIG.staticUrl}/feature-gate/secure_shares_portrait.png`,
  TEAM_ONLY_COMMENTS: `${CONFIG.staticUrl}/feature-gate/internal_comments_portrait.png`,
};

const mobileImages: Record<string, string> = {
  COMMENT_ATTACHMENTS: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-comments-mobile2.png`,
  CUSTOM_BRANDED_SHARES: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-branded-header-mobile2.png`,
  PRIVATE_PROJECTS: `${CONFIG.staticUrl}/feature-gate/restricted_projects_landscape.png`,
  REEL_PLAYER: `${CONFIG.staticUrl}/feature-gate/upgrade-modal-reel-mobile2.png`,
  RESOURCE_CONTROL_POLICIES: `${CONFIG.staticUrl}/feature-gate/enterprise_generic_landscape.png`,
  SECURE_SHARING: `${CONFIG.staticUrl}/feature-gate/secure_shares_landscape.png`,
  TEAM_ONLY_COMMENTS: `${CONFIG.staticUrl}/feature-gate/internal_comments_landscape.png`,
};

type PlanDetailsProps = {
  feature?: PlanFeatureInput;
  image?: string;
  isLoading?: boolean;
  paymentMethod?: PaymentCard;
  onClose?: () => void;
  setParentStep: Dispatch<SetStateAction<ACCOUNT_CONVERSION_STEPS>>;
} & Pick<
  ConversionDialogProps,
  | 'cost'
  | 'featureGateType'
  | 'period'
  | 'subscriptionUpdateSource'
  | 'tier'
  | 'title'
>;

const PLAN_ROUTE = '/settings/plan';
const BILLING_ROUTE = '/settings/billing';

const headerIconStyles: CSSProperties = {
  position: 'absolute',
  right: '-14px',
  top: '3px',
};

export function PlanDetails({
  cost,
  feature,
  featureGateType,
  isLoading = false,
  onClose = noop,
  paymentMethod,
  period,
  setParentStep,
  subscriptionUpdateSource,
  tier,
  title,
}: PlanDetailsProps) {
  const image =
    feature && images[feature]
      ? images[feature]
      : `${CONFIG.staticUrl}/feature-gate/upgrade-modal-all3.png`;
  const mobileImage =
    feature && mobileImages[feature]
      ? mobileImages[feature]
      : `${CONFIG.staticUrl}/feature-gate/upgrade-modal-all-mobile2.png`;

  const accountId = useActiveAccountId() ?? '';
  const accountOwner = useAccountOwner();
  const isEnterpriseSubscriber = useIsEnterpriseSubscriber();
  const isEnterprise = tier === 'ENTERPRISE' || isEnterpriseSubscriber;
  const [openInBrowser] = useOpenInBrowserMutation({
    context: { resolveWithUXP: true },
  });
  const isIntegrationClient = useIsIntegrationClient();

  const ownerName = accountOwner?.owner?.name ?? '';
  const ownerEmail = accountOwner?.owner?.email ?? '';

  const canManageBilling = useAccountPermissions({
    accountId,
    check: 'canManageBilling',
  });

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  const openCalendly = useOpenCalendly();

  const handleClick = () => {
    track('upgrade-modal-cta-selected', {
      ...currentPlanTracking,
      ...accountRoleTracking,
      feature: feature ? segmentTrackingValues.features[feature] : undefined,
      feature_gate_type: featureGateType,
      subscription_update_source: subscriptionUpdateSource,
    });

    if (isEnterprise) {
      openCalendly();
      onClose();
      return;
    }

    const parentStep = paymentMethod?.card
      ? ACCOUNT_CONVERSION_STEPS.SUMMARY
      : ACCOUNT_CONVERSION_STEPS.PAYMENT;

    setParentStep(parentStep);
  };

  const tierConfig = getTierConfig(tier);

  const managedByAdobe = useManagedByAdobe();

  const isAddon = isEnterpriseSubscriber;
  const showChatWithSales = isEnterprise;
  const showPricing = canManageBilling && !isEnterpriseSubscriber;
  const showComparePlans = canManageBilling && !isEnterpriseSubscriber;
  const canRenderCtaButton = canManageBilling;

  const getPriceText = () => {
    if (tier === 'ENTERPRISE') return TEXT.priceEnterprise;
    const formattedPrice = formatMoney(cost, false);
    return TEXT.pricePaid(formattedPrice, getPeriodText()[period]);
  };

  const getComparePlansText = () => {
    if (tier === 'ENTERPRISE') return TEXT.comparePlansEnterprise;
    return TEXT.comparePlans;
  };

  const comparePlansRoute = isEnterprise ? BILLING_ROUTE : PLAN_ROUTE;

  const onClickComparePlans = useCallback(
    (e: React.MouseEvent<HTMLElement>, path: string) => {
      const integrationsUrl = new URL(
        path,
        getV4AppServerOriginFromIntegrations()
      ).href;
      if (isIntegrationClient) {
        e.preventDefault();
        openInBrowser({
          variables: {
            input: {
              url: integrationsUrl,
            },
          },
        });
      }
    },
    [isIntegrationClient, openInBrowser]
  );

  const getReachOutToOwnerContent = () => {
    if (managedByAdobe) {
      return (
        <>
          <Text color="tertiary" variant="secondary">
            {TEXT.featureNotAvailableAdobe}
          </Text>
          <Text color="tertiary" variant="secondary">
            {TEXT.contactAdminAdobe}
          </Text>
        </>
      );
    }

    return (
      <Text color="tertiary" display="inline" variant="secondary">
        {TEXT.contactOwnerToUpgrade(ownerName, ownerEmail)}
      </Text>
    );
  };

  return (
    <UpgradeDialogLayout
      actions={
        <>
          {canRenderCtaButton && (
            <Button2
              block={true}
              data-testid="subscribe-to-plan-button"
              iconBefore={<IconStarFantasyFill />}
              isDisabled={isLoading}
              onPress={handleClick}
              variant="primary"
            >
              {showChatWithSales
                ? TEXT.chatWithSales
                : TEXT.upgradeHeader(title)}
            </Button2>
          )}

          <div className={styles.ctaDetailsContainer}>
            {!canManageBilling && getReachOutToOwnerContent()}

            {showPricing && (
              <Text color="secondary" display="inline" variant="secondary">
                {getPriceText()}
              </Text>
            )}
            {showComparePlans && (
              <StyledLink
                $inlineBlock={true}
                href={comparePlansRoute}
                onClick={(e) => onClickComparePlans(e, comparePlansRoute)}
                target="_blank"
              >
                <Text color="link" variant="secondary">
                  {getComparePlansText()}
                </Text>
              </StyledLink>
            )}
          </div>
        </>
      }
      closeLabel={TEXT.close}
      image={image}
      isWide={isEnterprise}
      mobileImage={mobileImage}
      onClose={onClose}
      subtitle={feature ? getSubHeaders()[feature] : undefined}
      title={
        isAddon ? (
          <>
            <span
              className={styles.highlightedSpan}
              style={{ color: tierConfig.color }}
            >
              {TEXT.enterprise}
              <IconStarFantasyFill color={tierConfig.color} />
            </span>{' '}
            {TEXT.addOn}
          </>
        ) : (
          <>
            {TEXT.upgradeTo}{' '}
            <span
              className={styles.highlightedSpan}
              style={{ color: tierConfig.color }}
            >
              {title}
              <IconStarFantasyFill
                color={tierConfig.color}
                size={16}
                style={headerIconStyles}
              />
            </span>
          </>
        )
      }
    >
      <PlanUpgradeFeatures feature={feature} tier={tier} />
    </UpgradeDialogLayout>
  );
}
